<template>
  <!-- ============================================
       VERIFY OTP PAGE
       OTP verification page for login flow
  ============================================ -->
  <div class="verify-otp-page">
    <MainNavbar />

    <!-- Toast Notifications -->
    <ToastContainer :toasts="toasts" @remove="removeToast" />

    <section class="verify-wrapper">
      <div class="verify-card">
        <h1 class="title">Enter Verification Code</h1>
        <p class="subtitle">
          A one-time code has been sent to your email:
          <strong>{{ email }}</strong>
        </p>

        <form class="verify-form" @submit.prevent="verifyOTP">
          <div class="form-group">
            <label for="otp-input">Verification Code (OTP)</label>
            <input
              id="otp-input"
              ref="otpInput"
              type="text"
              v-model="otp"
              inputmode="numeric"
              pattern="[0-9]*"
              maxlength="6"
              placeholder="Enter 6-digit code"
              :class="{ 'error-border': hasError }"
              :disabled="loading"
              :aria-label="'Enter 6-digit verification code sent to ' + email"
              :aria-invalid="hasError"
              :aria-describedby="hasError ? 'otp-error' : 'otp-hint'"
              autocomplete="one-time-code"
              required
              @input="onOtpInput"
              @keydown="onOtpKeydown"
            />
            <p v-if="otp.length > 0 && !hasError" id="otp-hint" class="hint">
              {{ 6 - otp.length }} character{{ (6 - otp.length) !== 1 ? 's' : '' }} remaining
            </p>
            <p v-if="hasError" id="otp-error" class="error-message" role="alert">
              <i class="fa-solid fa-circle-exclamation"></i>
              {{ errorMessage }}
            </p>
          </div>

          <button 
            class="submit-btn" 
            type="submit" 
            :disabled="loading || !isOtpValid"
          >
            <template v-if="!loading">
              <i class="fa-solid fa-check"></i>
              Verify & Sign In
            </template>
            <template v-else>
              <i class="fa-solid fa-spinner fa-spin"></i>
              Verifying...
            </template>
          </button>

          <div class="action-links">
            <button 
              type="button"
              class="back-link" 
              @click="goBack"
              :disabled="loading"
              aria-label="Go back to login page"
            >
              <i class="fa-solid fa-arrow-left"></i>
              Back to Sign In
            </button>
          </div>
        </form>
      </div>
    </section>

    <ThirdpartyFooter />
  </div>
</template>

<script>
/**
 * VerifyOTP Component
 * 
 * OTP verification page for login flow after initial login request.
 * Users enter the 6-digit OTP code received via email to complete authentication.
 * 
 * Features:
 * - 6-digit OTP input with numeric-only validation
 * - Real-time input sanitization and formatting
 * - Auto-focus on input field
 * - Toast notifications for errors and success
 * - Responsive design for all screen sizes
 * - Accessibility features (ARIA labels, keyboard navigation)
 * - Visual error states and feedback
 * - Loading states during verification
 * 
 * @component
 * @name VerifyOTP
 * 
 * Data Flow:
 * 1. User arrives from login page with email in query params
 * 2. User enters 6-digit OTP code
 * 3. Code is validated client-side (numeric, 6 digits)
 * 4. Code is sent to backend for verification
 * 5. On success, user is authenticated and redirected to main page
 * 
 * API Endpoints:
 * - POST /auth/login-verify - Verify OTP and complete login
 * 
 * Query Parameters:
 * - email: User's email address (required)
 * - remember: Whether to remember user (optional, currently unused)
 */

import MainNavbar from "../../components/main/headernfooter/header.vue";
import ThirdpartyFooter from "../thirdparty/Footer.vue";
import ToastContainer from "../../components/ui/ToastContainer.vue";
import toastMixin from "@/mixins/toastMixin";
import { useAuthStore } from "../../authen/auth";
import { useCartStore } from "../../cartstore/cartStore";
import { buildApiUrl } from "@/config/api";

export default {
  name: "VerifyOTP",
  mixins: [toastMixin],

  components: { 
    MainNavbar,
    ThirdpartyFooter,
    ToastContainer
  },

  data() {
    return {
      /**
       * User's email address from query parameters
       * @type {string}
       */
      email: this.$route.query.email || "",
      
      /**
       * OTP code entered by user
       * @type {string}
       */
      otp: "",
      
      /**
       * Whether form is currently submitting
       * @type {boolean}
       */
      loading: false,
      
      /**
       * Error message to display
       * @type {string}
       */
      errorMessage: "",
      
      /**
       * Array of toast notifications
       * @type {Array<Object>}
       */
    };
  },

  computed: {
    /**
     * Whether OTP input has an error
     * @returns {boolean}
     */
    hasError() {
      return !!this.errorMessage;
    },

    /**
     * Whether OTP is valid (6 numeric digits)
     * @returns {boolean}
     */
    isOtpValid() {
      return /^\d{6}$/.test(this.otp);
    },
  },

  mounted() {
    /**
     * Auto-focus OTP input on component mount
     */
    this.$nextTick(() => {
      if (this.$refs.otpInput) {
        this.$refs.otpInput.focus();
      }
    });

    /**
     * Validate email is present
     */
    if (!this.email) {
      this.showToast(
        'error',
        'Missing Email',
        'Email address is required. Please return to the login page.'
      );
      setTimeout(() => {
        this.$router.push({ name: "CustomerSignIn" });
      }, 2000);
    }
  },

  methods: {
    /**
     * Handles OTP input changes
     * Sanitizes input to numeric only and limits to 6 digits
     * 
     * @param {Event} event - Input event
     */
    onOtpInput(event) {
      // Clear error when user starts typing
      this.errorMessage = "";
      
      // Get input value and strip non-numeric characters
      let value = event.target.value.replace(/\D/g, '');
      
      // Limit to 6 digits
      if (value.length > 6) {
        value = value.slice(0, 6);
      }
      
      // Update model
      this.otp = value;
      
      // Auto-submit when 6 digits are entered
      if (value.length === 6 && !this.loading) {
        // Small delay to allow user to see the complete code
        setTimeout(() => {
          this.verifyOTP();
        }, 300);
      }
    },

    /**
     * Handles keyboard input in OTP field
     * Allows only numeric keys, backspace, delete, arrow keys, tab
     * 
     * @param {KeyboardEvent} event - Keyboard event
     */
    onOtpKeydown(event) {
      // Allow: backspace, delete, tab, escape, enter, arrow keys
      if ([8, 9, 27, 13, 46, 37, 38, 39, 40].indexOf(event.keyCode) !== -1 ||
          // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
          (event.keyCode === 65 && event.ctrlKey === true) ||
          (event.keyCode === 67 && event.ctrlKey === true) ||
          (event.keyCode === 86 && event.ctrlKey === true) ||
          (event.keyCode === 88 && event.ctrlKey === true)) {
        return;
      }
      // Ensure that it is a number and stop the keypress
      if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && (event.keyCode < 96 || event.keyCode > 105)) {
        event.preventDefault();
      }
    },

    /**
     * Verifies OTP code with backend
     * Handles authentication and redirects on success
     */
    async verifyOTP() {
      // Prevent double submission
      if (this.loading) return;

      // Validate OTP format
      if (!this.isOtpValid) {
        this.errorMessage = "Please enter a valid 6-digit code";
        this.showToast(
          'error',
          'Invalid Code',
          'Please enter a valid 6-digit verification code.'
        );
        return;
      }

      // Validate email is present
      if (!this.email) {
        this.showToast(
          'error',
          'Missing Email',
          'Email address is required. Please return to the login page.'
        );
        setTimeout(() => {
          this.$router.push({ name: "CustomerSignIn" });
        }, 2000);
        return;
      }

      this.loading = true;
      this.errorMessage = "";

      const authStore = useAuthStore();
      const cart = useCartStore();

      try {
        const response = await fetch(buildApiUrl("/auth/login-verify"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: this.email,
            otp: this.otp,
          }),
        });

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          // Extract error message from response
          const errorMsg = data.detail || data.message || "Incorrect OTP. Please try again.";
          this.errorMessage = errorMsg;
          
          this.showToast(
            'error',
            'Verification Failed',
            errorMsg
          );
          
          // Clear OTP input on error
          this.otp = "";
          this.$nextTick(() => {
            if (this.$refs.otpInput) {
              this.$refs.otpInput.focus();
            }
          });
          
          return;
        }

        // Success - save token and user session
        authStore.setAuthData(data.access_token, data.user);
        cart.handleLoginSession();

        this.showToast(
          'success',
          'Login Successful',
          'Welcome back! Redirecting...'
        );

        // Redirect after short delay to show success message
        setTimeout(() => {
          this.$router.push({ name: "MainPage" });
        }, 1000);

      } catch (error) {
        console.error("OTP Verification Error:", error);
        
        // Handle network errors
        if (error.name === 'TypeError' || error.message.includes('fetch')) {
          this.showToast(
            'error',
            'Network Error',
            'Unable to connect to the server. Please check your internet connection and try again.'
          );
        } else {
          this.showToast(
            'error',
            'Verification Error',
            'An unexpected error occurred. Please try again.'
          );
        }
        
        this.errorMessage = "Verification failed. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    /**
     * Navigates back to login page
     */
    goBack() {
      this.$router.push({ name: "CustomerSignIn" });
    },

  },
};
</script>

<style scoped>
/* ============================================
   MAIN PAGE CONTAINER
   Root container for verify OTP page
============================================ */
.verify-otp-page {
  width: 100%;
  min-height: 100vh;
  background: var(--background-color1);
  transition: background 0.25s ease;
  padding-top: 80px;
  display: flex;
  flex-direction: column;
}

/* Same footer flush as SignUp.vue / VerifyEmail.vue */
.verify-otp-page :deep(.thirdparty-footer) {
  margin-top: 0 !important;
  padding-top: 0 !important;
  align-self: stretch;
}

/* ============================================
   VERIFY WRAPPER
   Container for verify card
============================================ */
.verify-wrapper {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 2rem 1rem;
  min-height: 0;
}

.verify-card {
  width: 100%;
  max-width: 480px;
  background: var(--background-color2);
  color: var(--text-color1);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.verify-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
}

/* ============================================
   TITLE AND SUBTITLE
============================================ */
.title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--text-color1);
  line-height: 1.3;
}

.subtitle {
  color: var(--text-color2);
  margin-bottom: 2rem;
  font-size: 1rem;
  line-height: 1.5;
}

.subtitle strong {
  color: var(--text-color1);
  font-weight: 600;
}

/* ============================================
   FORM STYLES
============================================ */
.verify-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  text-align: left;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-color1);
  font-size: 0.95rem;
}

input {
  width: 100%;
  padding: 14px 16px;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-align: center;
  background: var(--background-color3);
  border: 2px solid var(--border-color-hover1);
  color: var(--text-color1);
  border-radius: 8px;
  outline: none;
  transition: all 0.25s ease;
  box-sizing: border-box;
}

input:focus {
  border-color: var(--border-color-hover);
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
  background: var(--background-color2);
}

input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: var(--background-color3);
}

input.error-border {
  border-color: #dc3545;
  background: #fff5f5;
}

input.error-border:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.1);
}

.hint {
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-color2);
}

.error-message {
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.error-message i {
  font-size: 0.85rem;
}

/* ============================================
   SUBMIT BUTTON
============================================ */
.submit-btn {
  width: 100%;
  padding: 14px 20px;
  font-size: 1.1rem;
  font-weight: 600;
  background: linear-gradient(135deg, var(--border-color-hover), var(--border-color-hover1));
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.25s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
  background: linear-gradient(135deg, var(--text-color1), var(--border-color-hover));
}

.submit-btn:active:not(:disabled) {
  transform: translateY(0);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.submit-btn i {
  font-size: 1rem;
}

/* ============================================
   ACTION LINKS
============================================ */
.action-links {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 0.5rem;
}

.back-link {
  background: transparent;
  border: none;
  color: var(--text-color2);
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: color 0.25s ease;
  text-decoration: none;
}

.back-link:hover:not(:disabled) {
  color: var(--border-color-hover);
}

.back-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.back-link i {
  font-size: 0.85rem;
}

/* ============================================
   RESPONSIVE DESIGN
============================================ */

/* Tablet */
@media (max-width: 1024px) {
  .verify-wrapper {
    padding: 2rem 1.5rem;
  }

  .verify-card {
    padding: 2.25rem 2rem;
  }

  .title {
    font-size: 1.6rem;
  }
}

/* Mobile Landscape */
@media (max-width: 768px) and (orientation: landscape) {
  .verify-wrapper {
    padding: 1.5rem 1rem;
  }

  .verify-card {
    padding: 1.75rem 1.5rem;
    max-width: 100%;
  }

  .title {
    font-size: 1.5rem;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    margin-bottom: 1.5rem;
    font-size: 0.95rem;
  }
}

/* Mobile Portrait */
@media (max-width: 480px) {
  .verify-wrapper {
    padding: 1.5rem 1rem;
  }

  .verify-card {
    padding: 2rem 1.5rem;
    border-radius: 12px;
  }

  .title {
    font-size: 1.4rem;
    margin-bottom: 0.6rem;
  }

  .subtitle {
    margin-bottom: 1.5rem;
    font-size: 0.9rem;
  }

  input {
    padding: 12px 14px;
    font-size: 1rem;
  }

  .submit-btn {
    padding: 12px 16px;
    font-size: 1rem;
  }

  .action-links {
    gap: 0.5rem;
  }

  .back-link {
    font-size: 0.85rem;
  }
}

/* Small Mobile */
@media (max-width: 360px) {
  .verify-card {
    padding: 1.5rem 1rem;
  }

  .title {
    font-size: 1.3rem;
  }

  .subtitle {
    font-size: 0.85rem;
  }

  input {
    padding: 10px 12px;
    font-size: 0.95rem;
  }

  .submit-btn {
    padding: 10px 14px;
    font-size: 0.95rem;
  }
}

/* Large Desktop */
@media (min-width: 1440px) {
  .verify-card {
    max-width: 520px;
    padding: 3rem;
  }

  .title {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 1.1rem;
  }
}

/* High DPI Displays */
@media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
  .verify-card {
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  }
}

/* Print Styles */
@media print {
  .verify-otp-page {
    padding-top: 0;
  }

  .verify-wrapper {
    padding: 1rem;
  }

  .verify-card {
    box-shadow: none;
    border: 1px solid #ddd;
  }

  .submit-btn,
  .back-link {
    display: none;
  }
}
</style>
